import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { useAppDispatch } from "../../../redux/store";
import { userActions } from "../../../redux/user/user.slice";
import { TestIDs } from "../../../tests/types/testIds";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { AtiraVerificationInput } from "../../../components/AtiraVerificationInput";
import { SignupDto } from "../../../model/user/dto/SignupDto";
import { GenericTokenDto } from "../../../model/user/dto/GenericTokenDto";
import { RESEND_COOLDOWN_KEY, clearPendingSignup } from "./signupProgress";

const TOO_MANY_ATTEMPTS_ERROR = "server-error/auth.signup.too-many-attempts";

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  margin-top: ${Spacing.m};

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const RestartButton = styled(Button)`
  background-color: ${(props) => props.theme.transparent};
  margin: 0;
  padding: 0;
  color: ${(props) => props.theme.main};
`;

type SignupTokenProps = {
  onRestart: VoidFunction;
  onSessionExpired: VoidFunction;
};

export const SignupToken: React.FC<SignupTokenProps> = ({
  onRestart,
  onSessionExpired,
}) => {
  const [loading, setLoading] = useState(false);
  // Bumped to remount AtiraVerificationInput after the server burns the code
  // (too many wrong guesses), so its resend cooldown re-reads storage.
  const [inputNonce, setInputNonce] = useState(0);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { isValid },
  } = useFormContext<SignupDto & GenericTokenDto>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();

  const onResendToken = async () => {
    try {
      const email = getValues("email");

      await dispatch(userActions.resendSignupToken({ email })).unwrap();
    } catch (e: any) {
      console.log(e);

      // The pending signup can no longer be recovered - send the user back to
      // step 1 (name + email stay prefilled). AtiraVerificationInput surfaces
      // the message.
      if (e?.response?.data === "server-error/auth.signup.no-verification") {
        onSessionExpired();
      }

      throw e;
    }
  };

  const onNext = async () => {
    try {
      setLoading(true);
      const token = getValues("token");
      const email = getValues("email");

      await dispatch(userActions.verifyUserAccount({ token, email })).unwrap();

      clearPendingSignup();

      AtiraToast.success(t("welcome_message"));

      const redirectPath = searchParams.get("redirect");

      navigate(redirectPath ? `/${redirectPath}` : `/dashboard${search}`, {
        replace: true,
      });
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);

      // Server burned the code after too many wrong guesses. Clear the dead
      // input and let the user resend right away (past deadline -> enabled).
      if (e?.response?.data === TOO_MANY_ATTEMPTS_ERROR) {
        setValue("token", "");
        try {
          sessionStorage.setItem(RESEND_COOLDOWN_KEY, "0");
        } catch {}
        setInputNonce((n) => n + 1);
        return;
      }

      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      width={"100%"}
      gap="s"
    >
      <Text color="black">{t("signup.verification.token")}</Text>
      <Controller
        name="token"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.fieldRequired"),
          },
          minLength: {
            value: 6,
            message: t("common.error.verificationCode.length"),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <AtiraVerificationInput
            key={inputNonce}
            value={value}
            onChange={onChange}
            valid={!error}
            errorMessage={error?.message}
            onResendToken={onResendToken}
            cooldownStorageKey={RESEND_COOLDOWN_KEY}
          />
        )}
      />
      <StyledButton
        onClick={handleSubmit(onNext)}
        loading={loading}
        disabled={!isValid}
        dataTestId={TestIDs.auth.signup.code.submit}
      >
        {t("signup.verification.button")}
      </StyledButton>

      <RestartButton
        onClick={onRestart}
        title={t("signup.start_over")}
        dataTestId={TestIDs.auth.signup.startOver}
      />
    </Flex>
  );
};
